import { JWTContextValue } from '../contexts/JWTProvider';

/**
 * Get appropriate contact text based on JWT state and service name
 */
export function getContactText(
  jwtState: JWTContextValue,
  serviceName?: string
): string {
  // If we have a service name from props/state, use it (highest priority)
  if (serviceName) {
    return serviceName;
  }

  // If JWT has a service name, use it
  if (jwtState.serviceName) {
    return jwtState.serviceName;
  }

  // Fallback when no service name is available
  return 'the team that provided you with this link';
}

/**
 * Get user-friendly error message based on JWT state
 */
export function getJWTErrorMessage(jwtState: JWTContextValue): string {
  switch (jwtState.errorType) {
    case 'expired':
      return 'The connection link has expired';
    case 'missing':
    case 'malformed':
    case 'invalid_signature':
    default:
      return 'The connection link is invalid';
  }
}

/**
 * Get user-friendly error caption based on JWT state
 */
export function getJWTErrorCaption(
  jwtState: JWTContextValue,
  serviceName?: string
): string {
  const contactText = getContactText(jwtState, serviceName);

  // If we can redirect back to service, mention that option
  if (jwtState.canRedirectToService) {
    return `Please contact ${contactText} for a new connection link, or return to the service to try again.`;
  }

  // If we can't redirect, just mention contacting for new link
  return `Please contact ${contactText} for a new connection link.`;
}

/**
 * Get analytics context from JWT state
 */
export function getAnalyticsContext(
  jwtState: JWTContextValue,
  serviceName?: string
) {
  return {
    serviceName: serviceName,
    serviceId: jwtState.serviceId,
    fleetCode: jwtState.fleetCode,
    jwtState: {
      exists: jwtState.exists,
      isValid: jwtState.isValid,
      isExpired: jwtState.isExpired,
      isDecodable: jwtState.isDecodable,
      errorType: jwtState.errorType,
      canRedirectToService: jwtState.canRedirectToService,
    },
  };
}

/**
 * Determine appropriate navigation action based on JWT state
 */
export function getNavigationAction(jwtState: JWTContextValue): {
  primaryAction: 'try_again' | 'return_to_service' | null;
  secondaryAction: 'return_to_service' | 'select_provider' | null;
  primaryLabel: string | null;
  secondaryLabel: string | null;
} {
  // Valid JWT - user can try again or select different provider
  if (jwtState.isValid) {
    return {
      primaryAction: 'try_again',
      secondaryAction: 'select_provider',
      primaryLabel: 'TRY AGAIN',
      secondaryLabel: 'SELECT PROVIDER',
    };
  }

  // Invalid/expired JWT with redirect info - send them back to service
  if (jwtState.canRedirectToService) {
    return {
      primaryAction: 'return_to_service',
      secondaryAction: null,
      primaryLabel: 'RETURN TO SERVICE',
      secondaryLabel: null,
    };
  }

  // Invalid/expired JWT without redirect info - no actionable buttons
  // User will need to use browser back button or close tab
  return {
    primaryAction: null,
    secondaryAction: null,
    primaryLabel: null,
    secondaryLabel: null,
  };
}

/**
 * Get service display name from JWT state
 */
export function getServiceDisplayName(
  jwtState: JWTContextValue,
  fallbackServiceName?: string
): string {
  // In the future, we could map serviceId to actual service names
  // For now, use the fallback service name or a generic label
  if (fallbackServiceName) {
    return fallbackServiceName.toUpperCase();
  }

  if (jwtState.serviceId) {
    return `SERVICE ${jwtState.serviceId}`;
  }

  return 'SERVICE';
}

/**
 * Check if user should be redirected to error page based on JWT state
 */
export function shouldRedirectToError(jwtState: JWTContextValue): boolean {
  return !jwtState.isValid && !jwtState.canRedirectToService;
}

/**
 * Get appropriate redirect path based on JWT state and current page
 */
export function getRedirectPath(
  jwtState: JWTContextValue,
  currentPath: string
): string {
  if (jwtState.isValid) {
    // Valid token - continue with normal flow
    if (currentPath === '/') return '/selectTSP';
    return currentPath;
  }

  if (jwtState.isExpired || jwtState.isDecodable) {
    // Expired or decodable token - go to approval page for refresh
    return '/';
  }

  // Invalid token - go to error page
  return '/error';
}

/**
 * Create link state for OAuth redirects
 */
export function createOAuthLinkState(
  jwtState: JWTContextValue,
  updatingTSP: boolean,
  magicLink: boolean,
  serviceName?: string
): string {
  const token = jwtState.rawToken || 'null';
  const serviceID = jwtState.serviceId?.toString() || 'null';
  const fleetCode = jwtState.fleetCode || 'null';

  return [
    token,
    updatingTSP.toString(),
    magicLink.toString(),
    serviceID,
    fleetCode,
  ].join(',');
}

/**
 * Validate JWT state for specific operations
 */
export const jwtValidators = {
  /**
   * Check if JWT is valid for onboarding operations
   */
  canOnboard: (jwtState: JWTContextValue): boolean => {
    return (
      jwtState.isValid &&
      jwtState.serviceId !== null &&
      jwtState.fleetCode !== null
    );
  },

  /**
   * Check if JWT has enough info for analytics
   */
  canSetupAnalytics: (jwtState: JWTContextValue): boolean => {
    return (
      jwtState.isDecodable &&
      (jwtState.serviceId !== null || jwtState.fleetCode !== null)
    );
  },

  /**
   * Check if JWT can be used for API calls
   */
  canMakeAPICall: (jwtState: JWTContextValue): boolean => {
    return jwtState.isValid && jwtState.rawToken !== null;
  },

  /**
   * Check if user can be redirected back to service
   */
  canReturnToService: (jwtState: JWTContextValue): boolean => {
    return (
      jwtState.canRedirectToService && jwtState.preferredRedirectURI !== null
    );
  },
};
